/**
 * Utilidad de cache para reportes en el navegador
 * Almacena temporalmente los resultados de reportes grandes
 */

class ReportesCacheService {
  constructor() {
    this.prefix = 'mdz_reportes_cache_';
    this.maxAge = 30 * 60 * 1000; // 30 minutos
    this.maxSize = 10 * 1024 * 1024; // 10 MB (límite aproximado)
  }

  /**
   * Genera una clave única basada en los parámetros del reporte
   */
  generateKey(params) {
    const keyData = {
      tipo: params.tipoOperatoria || 'default',
      fideicomisos: params.fideicomisosId || '',
      operatorias: params.operatoriasIds || '',
      estados: params.estadosIds || '',
      fechaDesde: params.fechaDesde || '',
      fechaHasta: params.fechaHasta || '',
      page: params.page || 1,
      limit: params.limit || 50,
    };

    // Crear string único y corto
    const keyString = JSON.stringify(keyData);
    return this.prefix + this.hashCode(keyString);
  }

  /**
   * Genera un hash simple para la clave
   */
  hashCode(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Guarda datos en el cache
   */
  set(params, data) {
    try {
      const key = this.generateKey(params);
      const cacheData = {
        data: data,
        timestamp: Date.now(),
        params: params,
      };

      const jsonData = JSON.stringify(cacheData);

      // Verificar tamaño antes de guardar
      if (jsonData.length > this.maxSize) {
        console.warn('⚠️ Los datos son demasiado grandes para el cache del navegador');
        return false;
      }

      localStorage.setItem(key, jsonData);
      console.log('✅ Datos guardados en cache:', key, 'Size:', (jsonData.length / 1024).toFixed(2), 'KB');

      // Limpiar cache antiguo
      this.cleanOldCache();
      return true;
    } catch (error) {
      console.error('❌ Error al guardar en cache:', error);

      // Si es error de cuota, limpiar todo el cache
      if (error.name === 'QuotaExceededError') {
        console.warn('⚠️ Cuota de localStorage excedida, limpiando cache...');
        this.clearAll();
      }
      return false;
    }
  }

  /**
   * Obtiene datos del cache
   */
  get(params) {
    try {
      const key = this.generateKey(params);
      const cached = localStorage.getItem(key);

      if (!cached) {
        console.log('ℹ️ No hay datos en cache para:', key);
        return null;
      }

      const cacheData = JSON.parse(cached);
      const age = Date.now() - cacheData.timestamp;

      // Verificar si expiró
      if (age > this.maxAge) {
        console.log('⏰ Cache expirado, eliminando:', key);
        localStorage.removeItem(key);
        return null;
      }

      console.log('✅ Datos obtenidos del cache:', key, 'Age:', (age / 1000).toFixed(0), 's');
      return cacheData.data;
    } catch (error) {
      console.error('❌ Error al leer del cache:', error);
      return null;
    }
  }

  /**
   * Verifica si existe cache válido para los parámetros
   */
  has(params) {
    return this.get(params) !== null;
  }

  /**
   * Elimina una entrada específica del cache
   */
  remove(params) {
    try {
      const key = this.generateKey(params);
      localStorage.removeItem(key);
      console.log('🗑️ Cache eliminado:', key);
      return true;
    } catch (error) {
      console.error('❌ Error al eliminar del cache:', error);
      return false;
    }
  }

  /**
   * Limpia entradas antiguas del cache
   */
  cleanOldCache() {
    try {
      const now = Date.now();
      const keys = Object.keys(localStorage);
      let cleaned = 0;

      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          try {
            const cached = localStorage.getItem(key);
            const cacheData = JSON.parse(cached);
            const age = now - cacheData.timestamp;

            if (age > this.maxAge) {
              localStorage.removeItem(key);
              cleaned++;
            }
          } catch (error) {
            // Si hay error al parsear, eliminar la entrada corrupta
            localStorage.removeItem(key);
            cleaned++;
          }
        }
      });

      if (cleaned > 0) {
        console.log('🧹 Cache limpiado:', cleaned, 'entradas antiguas eliminadas');
      }
    } catch (error) {
      console.error('❌ Error al limpiar cache:', error);
    }
  }

  /**
   * Elimina todo el cache de reportes
   */
  clearAll() {
    try {
      const keys = Object.keys(localStorage);
      let cleared = 0;

      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
          cleared++;
        }
      });

      console.log('🧹 Todo el cache eliminado:', cleared, 'entradas');
      return cleared;
    } catch (error) {
      console.error('❌ Error al eliminar todo el cache:', error);
      return 0;
    }
  }

  /**
   * Obtiene estadísticas del cache
   */
  getStats() {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith(this.prefix));

      let totalSize = 0;
      let validEntries = 0;
      let expiredEntries = 0;
      const now = Date.now();

      cacheKeys.forEach(key => {
        try {
          const cached = localStorage.getItem(key);
          totalSize += cached.length;

          const cacheData = JSON.parse(cached);
          const age = now - cacheData.timestamp;

          if (age > this.maxAge) {
            expiredEntries++;
          } else {
            validEntries++;
          }
        } catch (error) {
          // Ignorar entradas corruptas
        }
      });

      return {
        totalEntries: cacheKeys.length,
        validEntries,
        expiredEntries,
        totalSizeKB: (totalSize / 1024).toFixed(2),
        maxAgeMinutes: this.maxAge / 60000,
      };
    } catch (error) {
      console.error('❌ Error al obtener estadísticas:', error);
      return null;
    }
  }

  /**
   * Invalida el cache cuando cambian los filtros principales
   */
  invalidateRelated(fideicomiso, operatoria) {
    try {
      const keys = Object.keys(localStorage);
      let invalidated = 0;

      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          try {
            const cached = localStorage.getItem(key);
            const cacheData = JSON.parse(cached);

            // Invalidar si coincide fideicomiso u operatoria
            if (cacheData.params.fideicomisosId === fideicomiso ||
                cacheData.params.operatoriasIds === operatoria) {
              localStorage.removeItem(key);
              invalidated++;
            }
          } catch (error) {
            // Ignorar errores
          }
        }
      });

      if (invalidated > 0) {
        console.log('♻️ Cache invalidado:', invalidated, 'entradas relacionadas');
      }
      return invalidated;
    } catch (error) {
      console.error('❌ Error al invalidar cache relacionado:', error);
      return 0;
    }
  }
}

export default new ReportesCacheService();
